import { FlightSummary } from '@app/models';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { LoadingService } from '../loading.service';
import { catchError, lastValueFrom, map, Observable, of, throwError } from 'rxjs';
import { Reservation } from '@app/models/micro/reservation.model';
import { TypeLoading } from '@app/enums';
import { AncillariesMapper } from '@app/mappers/micro/acillaries.mapper';
import { Injectable } from '@angular/core';
import {
  AcillaryResponseInt,
  AncillariesResponseInt,
  BaggageAndServicesInt,
  BenefitBySegmentResponseInt,
} from '@app/interfaces/micro/ancillaries.interface';
import { Passenger } from '@app/models/micro';
import { AcillariesMapper } from '@app/mappers/acillaries.mapper';
import { SeatMapsMapper } from '@app/mappers';
import { ErrorAdapter } from '@models/adapter/error.adapter';

type TypeRedeem = 'tier' | 'cobrand' | 'corporate' | 'f&f';
@Injectable({
  providedIn: 'root',
})
export class ApiAncillaries {
  /** BFF de ancillaries (sin cifrar por ahora). */
  urlAncillariesBFF = `${environment.urlApiAncillariesBFF}${environment.versionAncillariesBFF}/kiosk/ancillaries`;
  /** Micro de ancillaries: PUT /ac/ancillaries/v2/{pnr} no entra al BFF. */
  urlAncillaries = `${environment.urlAncillaries}`;
  urlCheckin: string = `${environment.urlApiCheckin}`;
  constructor(private _loadingService: LoadingService, private _http: HttpClient) {}

  /**
   * Manejo de errores HTTP para las peticiones.
   * @param {HttpErrorResponse} error - Respuesta de error HTTP
   * @returns {Observable<never>} Observable con error procesado
   */

  handlerError(error: HttpErrorResponse, loading = TypeLoading.FOOTER_LOADING) {
    this._loadingService.hideLoading(loading);
    const adapt = ErrorAdapter.adapt(error.error);
    return throwError(adapt);
  }

  getMaps(reservation: Reservation) {
    const body = AncillariesMapper.seatMapRequest(reservation);
    const headers = new HttpHeaders({
      channel: environment.channel,
      flow: 'checkin',
      store: 'mx',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'x-transactionId': reservation.flightSummary.cartId,
      reservationCode: reservation.pnr,
    });
    this._loadingService.showLoading(TypeLoading.FULL_LOADING);
    return this._http.post(`${this.urlAncillariesBFF}/seatmap-offer-seats`, body, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
        return SeatMapsMapper.getSeatMaps(res as any);
      }),
      catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
    );
  }

  getAncillaries(reservation: Reservation) {
    const headers = new HttpHeaders({
      // reservationCode: reservation.pnr,
      channel: environment.channel,
      'x-transactionId': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      flow: 'myb',
      store: 'mx',
    });
    const { flightSummary } = reservation;
    const body = AncillariesMapper.getAncillariesRequest(reservation);

    this._loadingService.showLoading(TypeLoading.COMPONENT_LOADING);
    return this._http
      .post<AncillariesResponseInt>(`${this.urlAncillariesBFF}/ancillaries-offer-baggage`, body, {
        headers,
      })
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.COMPONENT_LOADING);
          AncillariesMapper.applyFfTierFromAncillaries(res, reservation);
          return AncillariesMapper.getAncillariesResponse(res, flightSummary.legCode);
        }),
        catchError((error) => {
          // CI-18515: Log del error
          console.error(' Error en /kiosk/ancillaries:', {
            status: error?.status,
            statusText: error?.statusText,
            error: error?.error,
            message: error?.message,
          });
          return this.handlerError(error, TypeLoading.COMPONENT_LOADING);
        })
      );
  }

  addAncillaries(reservation: Reservation): Observable<any> {
    const headers = this.getHeaders(reservation);
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);

    const body = AcillariesMapper.getAncillariesRequest(reservation);
    return this._http
      .put(`${this.urlAncillaries}/ac/ancillaries/v2/${reservation.pnr}`, body, { headers })
      .pipe(
        map((response) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return AncillariesMapper.mapResponse(response);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FOOTER_LOADING))
      );
  }

  redeemAncillaries(
    reservation: Reservation,
    passenger: Passenger,
    ancillary: any,
    type: TypeRedeem
  ) {
    const headers = new HttpHeaders({
      channel: environment.channel,
      store: 'mx',
      'x-transactionId': reservation.flightSummary.cartId,
      flow: 'myb',
    });
    const body = AncillariesMapper.redeemAncillariesRequest(
      reservation,
      passenger,
      ancillary,
      type
    );
    this._loadingService.showLoading(TypeLoading.COMPONENT_LOADING);
    return this._http
      .put(`${this.urlAncillaries}/ac/ancillaries/v2/${reservation.pnr}`, body, { headers })
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.COMPONENT_LOADING);
          return AncillariesMapper.mapResponse(res);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.COMPONENT_LOADING))
      );
  }

  removeAncillaries(reservation: Reservation): Observable<any> {
    this._loadingService.showLoading(TypeLoading.FULL_LOADING);
    const body = AncillariesMapper.removeAncillariesRequest(reservation);

    // Validar si el listado de ancillaries está vacío
    const hasAncillaries = body.travelerInfo?.some(
      (traveler) => traveler.ancillaries && traveler.ancillaries.length > 0
    );

    // Si no hay ancillaries, no ejecutar la llamada
    if (!hasAncillaries) {
      this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
      return of(null);
    }

    const headers = new HttpHeaders({
      channel: environment.channel,
      'x-transactionId': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      flow: 'myb',
      store: 'mx',
    });
    return this._http
      .put<any>(`${this.urlAncillaries}/ac/ancillaries/v2/${reservation.pnr}`, body, {
        headers,
      })
      .pipe(
        map((response) => {
          this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
          return AncillariesMapper.mapRemoveAncillariesResponse(response);
        }),
        catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
      );
  }

  setSeat(reservation: Reservation, currentPassenger: Passenger, seatSelected, seatOld) {
    const headers = new HttpHeaders({
      reservationCode: reservation.pnr,
      channel: environment.channel,
      flow: 'myb',
      store: 'mx',
      'x-transactionId': reservation.flightSummary.cartId,
    });

    const body = AncillariesMapper.setSeatRequest(
      reservation,
      currentPassenger,
      seatSelected,
      seatOld,
      false
    );
    this._loadingService.showLoading(TypeLoading.FULL_LOADING);

    return this._http.post(`${this.urlAncillariesBFF}/seat-seating`, body, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
        return AncillariesMapper.respSeat(res);
      }),
      catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
    );
  }

  removeSeat(reservation: Reservation) {
    this._loadingService.showLoading(TypeLoading.FULL_LOADING);
    const headers = new HttpHeaders({
      reservationCode: reservation.pnr,
      channel: environment.channel,
      flow: 'myb',
      store: 'mx',
      'x-transactionId': reservation.flightSummary.cartId,
    });

    const body = AncillariesMapper.removeAllSeatsRequest(reservation);
    return this._http.post(`${this.urlAncillariesBFF}/seat-seating`, body, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FULL_LOADING);
        return AncillariesMapper.respSeat(res);
      }),
      catchError((error) => this.handlerError(error, TypeLoading.FULL_LOADING))
    );
  }

  /** @deprecated Usar getBenefitsBySegment. Se conserva para rollback. */
  getAcillariesFare(reservation: Reservation) {
    const { flightSummary, passengers, segments, isEmployee, isStandBy, isNewBF } = reservation;
    const { legCode, legRegion } = flightSummary;
    const { fareFamily } = flightSummary.currentSegment;
    const firstSegment = segments[0];
    const aeroSegment =
      segments.find((segment) => segment.operatingCarrier === 'AM') ?? firstSegment;
    const tickets = passengers.map((passenger) => passenger?.ticketInfo?.eticket).join(',');
    const route = `${aeroSegment.departureAirport}-${aeroSegment.arrivalAirport}`;
    const headers = this.getHeaders(reservation)
      .set('isEmployee', isEmployee.toString())
      .set('isStandBy', isStandBy.toString())
      .set('legCode', legCode)
      .set('legRegion', legRegion)
      .set('route', route)
      .set('tickets', tickets)
      .set('isNewBF', isNewBF.toString())
      .set('fareFamily', fareFamily);

    return this._http
      .get<BaggageAndServicesInt>(`${this.urlCheckin}/tc/benefits`, { headers })
      .pipe(
        map((res) => AncillariesMapper.mapAncillariesFare(res, reservation)),
        catchError((error) => this.handlerError(error))
      );
  }

  /** Mismo mapper de respuesta que getAcillariesFare */
  getBenefitsBySegment(reservation: Reservation) {
    const headers = this.getHeaders(reservation);
    const body = AncillariesMapper.buildBenefitBySegmentRequest(reservation);
    const currentSegmentCode = reservation.flightSummary.currentSegment.segmentCode;

    return this._http
      .post<BenefitBySegmentResponseInt>(`${this.urlCheckin}/tc/benefits/benefitBySegment`, body, {
        headers,
      })
      .pipe(
        map((res) => {
          const benefits = AncillariesMapper.unwrapBenefitBySegmentResponse(
            res,
            currentSegmentCode
          );
          return AncillariesMapper.mapAncillariesFare(benefits, reservation);
        }),
        catchError((error) => this.handlerError(error))
      );
  }

  getHeaders(reservation: Reservation, flow = 'checkin') {
    let headers = new HttpHeaders({
      channel: environment.channel,
      flow: flow,
      cacheEnabled: 'false',
      'x-transactionId': reservation.flightSummary.cartId,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      store: 'mx',
      reservationCode: reservation.pnr,
    });

    return headers;
  }
}
